import React, { useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { HttpClient } from '../api/httpClient';
import {
  setHtsAdditonalInfo,
  tariffLoader,
  updateTariffs,
} from '../actions/DescriptionOfMerchandiseActions';
import moment from 'moment';
import { confirmAlert } from 'react-confirm-alert';
import { getHtsMetadataBatch } from '../api/htsRequests';
import { showToast } from '../util/showToast';

function getEntryDateUsingPriority({ releaseDate, itDate, entryDate }) {
  return [itDate, releaseDate, entryDate, 0].find((date) => date >= 1000) || 0;
}

function getUtcDateTimestampFromEpoch(epoch) {
  if (!epoch) return '';

  const date = new Date(epoch);
  const formattedDateStr = `${
    date.getUTCMonth() + 1
  }/${date.getUTCDate()}/${date.getUTCFullYear()}`;
  return new Date(formattedDateStr).getTime();
}

function UpdateTariffs({ children, invoiceId, merchandiseId, customConfig: storedCustomConfig }) {
  const dispatch = useDispatch();
  const isProcessingRef = useRef(false);
  const currentProcessingKeyRef = useRef(null);
  
  // ✅ PERFORMANCE FIX: Combine selectors to reduce re-renders
  const invoices = useSelector((state) => state?.DescriptionOfMerchandise?.entrySummaryInvoices);
  const entryTypeValues = useSelector((state) => state?.EntryTypeInfo);
  const bolInfo = useSelector((state) => state?.BOLInfo);

  // ✅ PERFORMANCE FIX: Memoize expensive find operation
  const currentInvoice = useMemo(
    () => invoices?.find((inv) => inv?.invoiceId === invoiceId),
    [invoices, invoiceId]
  );

  // ✅ PERFORMANCE FIX: Memoize expensive find/filter operations
  const merchandiseLineItem = useMemo(
    () => currentInvoice?.merchandiseLineItems?.find(
      (lineItem) => lineItem?.merchandiseId === merchandiseId
    ),
    [currentInvoice, merchandiseId]
  );

  const baseHts = useMemo(
    () => merchandiseLineItem?.merchandiseDuties?.find(
      (duty) => duty.isCommodityDuty === 1
    ),
    [merchandiseLineItem]
  );

  const additionalHts = useMemo(
    () => merchandiseLineItem?.merchandiseDuties?.filter(
      (duty) => duty.isCommodityDuty === 2
    ) || [],
    [merchandiseLineItem]
  );

  // exact logic for entry date
  const itDate = bolInfo?.itDate?.epochs
  
  const releaseDate = entryTypeValues?.releaseDate?.epochs
  
  const entryDate = entryTypeValues?.summaryDate?.epochs
  //updateHTS
  const updateHTS = async (payload) => {
    // Create a unique key for this operation to prevent duplicate calls
    // Include timestamp to make it unique for rapid successive calls
    const operationKey = `${invoiceId}-${merchandiseId}-${payload?.lineItemIndex}-${payload?.dutyIndex || ''}-${payload?.htsNo || ''}`;
    
    // Prevent duplicate calls - if already processing ANY operation, return early
    // This prevents multiple confirm dialogs when user switches apps
    if (isProcessingRef.current) {
      console.log('UpdateTariffs: Operation already in progress, preventing duplicate call');
      return;
    }
    
    // Mark as processing
    isProcessingRef.current = true;
    currentProcessingKeyRef.current = operationKey;
    try {
      const htsCode = payload?.htsNo || baseHts?.htsus;
      const countryOfOrigin =
        payload?.countryOfOrigin || merchandiseLineItem?.countryOfOrigin;
      const countryOfSmelt =
        merchandiseLineItem?.importerAdditionalDeclarationDetails?.importerAdditionalDeclarationDetail
          ?.find((d) => d.typeCode === 'ALUMINUM_SMELT_AND_CAST_COUNTRY_DETAIL')
          ?.aluminumSmeltAndCastCountryDetail?.primaryCountryOfSmelt?.trim() ||
        merchandiseLineItem?.importerAdditionalDeclarationDetails?.importerAdditionalDeclarationDetail?.find(
          (d) => d.typeCode === 'STEEL_MELT_AND_POUR_COUNTRY_DETAIL'
        )?.steelMeltAndPourCountryDetail?.countryOfMeltAndPourCode;

      const countryOfCast =
        payload?.countryOfCast ||
        merchandiseLineItem?.importerAdditionalDeclarationDetails
          ?.importerAdditionalDeclarationDetail?.[0]
          ?.aluminumSmeltAndCastCountryDetail?.countryOfCast;
      const tradeAgreement =
        payload?.tradeAgreement || merchandiseLineItem?.productClaimCode;
      if (Object.keys(payload).includes('htsNo') && !payload?.htsNo) return;
      dispatch(tariffLoader(true));
      // Use payload customConfig if provided, otherwise use stored one
      const customConfig = payload?.customConfig || storedCustomConfig || undefined

      const entryDateEpoch = getEntryDateUsingPriority({
        releaseDate,
        itDate,
        entryDate,
      });

      const response = await getHtsMetadataBatch({
        data: {
          eventMessage: {
            htsCode,
            countryOfOrigin,
            smeltCountryOfOrigin: (countryOfSmelt || countryOfCast)?.toUpperCase() !== 'N/A' ? (countryOfSmelt || countryOfCast) : undefined,
            tradeAgreement: ['NA', 'NONE']?.includes(tradeAgreement)
              ? undefined
              : tradeAgreement,
            entryDate: entryDateEpoch
              ? moment.utc(entryDateEpoch).format('MM-DD-YYYY')
              : '',
            customConfig: customConfig
          },
          merchandiseDutyId: baseHts?.merchandiseDutyId,
        },
      });
      const responseData = response?.data?.eventMessage?.responseMap?.[baseHts?.merchandiseDutyId];
      if(responseData?.primaryHtsMetadata?.header?.code == 404){
        // showToast(responseData?.primaryHtsMetadata?.header?.msg);
        dispatch(tariffLoader(false));
        isProcessingRef.current = false;
        currentProcessingKeyRef.current = null;
        return;
      }
      const tariffs = responseData?.additionalHtsMetadata || [];
      const {invoiceItemIndex, lineItemIndex, dutyIndex}=payload;
      const hasTariffsAndAdditionalHts = tariffs?.length && additionalHts?.length;
      const countryOfOriginChanged = payload?.countryOfOrigin !== undefined && 
        merchandiseLineItem?.countryOfOrigin;
      
      if (hasTariffsAndAdditionalHts || countryOfOriginChanged) {
        // Double-check we're still processing the same operation
        // This prevents showing confirm dialog if another operation started
        if (!isProcessingRef.current || currentProcessingKeyRef.current !== operationKey) {
          console.log('UpdateTariffs: Operation cancelled or superseded, skipping confirmation');
          dispatch(tariffLoader(false));
          isProcessingRef.current = false;
          currentProcessingKeyRef.current = null;
          return;
        }
        
        // Show confirm dialog - window.confirm is blocking, so only one can show at a time
        const userConfirmed = await new Promise((resolve) => {
          confirmAlert({
            title: "Confirm",
            message: "Apply tariffs from the HTS code and overwrite the existing ones?",
            closeOnClickOutside: false,
            buttons: [
              {
                label: "Yes",
                onClick: () => {
                  resolve(true);
                },
              },
              {
                label: "No",
                onClick: () => {
                  dispatch(tariffLoader(false));
                  isProcessingRef.current = false;
                  currentProcessingKeyRef.current = null;
                  resolve(false);
                },
              },
            ],
          });
        });
        
        if (!userConfirmed) {
          return;
        }
        
        // Verify again after confirm (in case user took a long time and another call started)
        if (!isProcessingRef.current || currentProcessingKeyRef.current !== operationKey) {
          console.log('UpdateTariffs: Operation superseded after confirmation, aborting');
          dispatch(tariffLoader(false));
          isProcessingRef.current = false;
          currentProcessingKeyRef.current = null;
          return;
        }
      }
      dispatch(setHtsAdditonalInfo({invoiceItemIndex, lineItemIndex, dutyIndex, data: response?.data?.eventMessage?.responseMap?.[baseHts?.merchandiseDutyId] || {}}))
      await dispatch(
        updateTariffs({ tariffs, invoiceId, merchandiseId, additionalHts })
      );
      dispatch(tariffLoader(false));
    } catch (e) {
      console.log(e);
    } finally {
      // Reset processing flag and loader
      isProcessingRef.current = false;
      currentProcessingKeyRef.current = null;
      dispatch(tariffLoader(false));
    }
  };

  return <div>{children({ updateHTS: updateHTS })}</div>;
}

export default UpdateTariffs;
